// src/components/TemporadasManager.js
// Pantalla para gestionar temporadas (periodos para clasificar series de entrenamientos)

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Plus, Edit2, Trash2, Power, Users, Search, Filter, ChevronDown, ChevronUp, MoreHorizontal } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from './ui/dropdown-menu';
import { usePermissions } from '../hooks/usePermissions';
import { useModal } from '../hooks/useModal';
import { useOrganization } from '../contexts/OrganizationContext';
import FeaturedLayout from './layouts/FeaturedLayout';
import { Button } from './ui/button';
import { Card, CardContent, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Alert, AlertDescription } from './ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import Modal from './ui/Modal';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';
import { useDeportes } from '../hooks/useDeportes';
import { opcionesDeporte, SIN_DEPORTES_MSG } from '../lib/deportesOrg';
import DeporteFijo from './deportes/DeporteFijo';
import {
  getTemporadas,
  createTemporada,
  updateTemporada,
  deleteTemporada,
  abrirTemporada,
  cerrarTemporada,
  getOrgDeportes,
} from '../lib/supabase';
import { parseLocalDate, getMexicoDateString } from '../lib/dateUtils';
import { ESTADO_LABELS, puedeEditarTemporada } from '../constants/temporadas';

const TemporadasManager = () => {
  const { label: deporteLabel } = useDeportes();
  const navigate = useNavigate();
  const { isAdmin } = usePermissions();
  const { modal, showConfirm, showError, showSuccess, closeModal } = useModal();

  const [temporadas, setTemporadas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFormModal, setShowFormModal] = useState(false);
  const [temporadaToEdit, setTemporadaToEdit] = useState(null);
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [estadoFilter, setEstadoFilter] = useState('todas');
  const [deporteFilter, setDeporteFilter] = useState('todos');

  const loadTemporadas = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await getTemporadas(false);
      if (error) throw error;
      setTemporadas(data || []);
    } catch (error) {
      console.error('Error cargando temporadas:', error);
      showError('Error', 'Error cargando temporadas');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadTemporadas();
  }, [loadTemporadas]);

  const handleNuevaTemporada = () => {
    setTemporadaToEdit(null);
    setShowFormModal(true);
  };

  const handleEditarTemporada = (temporada) => {
    setTemporadaToEdit(temporada);
    setShowFormModal(true);
  };

  const handleCambiarEstado = async (temporada, nuevoEstado) => {
    try {
      const fn = nuevoEstado === 'abierta' ? abrirTemporada : cerrarTemporada;
      const { error } = await fn(temporada.id);
      if (error) throw error;
      showSuccess('Éxito', nuevoEstado === 'abierta' ? 'Temporada abierta' : 'Temporada cerrada');
      loadTemporadas();
    } catch (error) {
      console.error('Error cambiando estado de temporada:', error);
      showError('Error', 'No se pudo cambiar el estado de la temporada');
    }
  };

  const handleEliminarTemporada = async (temporada) => {
    const confirmado = await showConfirm(
      'Confirmar eliminación',
      `¿Estás seguro de que deseas eliminar la temporada "${temporada.nombre}"? Las series asociadas no se eliminarán.`,
      'Eliminar',
      'Cancelar'
    );
    if (!confirmado) return;
    try {
      const { error } = await deleteTemporada(temporada.id);
      if (error) throw error;

      showSuccess('Éxito', 'Temporada eliminada correctamente');
      loadTemporadas();
    } catch (error) {
      console.error('Error eliminando temporada:', error);
      showError('Error', 'Error al eliminar la temporada');
    }
  };

  const handleFormSuccess = () => {
    setShowFormModal(false);
    setTemporadaToEdit(null);
    loadTemporadas();
    showSuccess('Éxito', temporadaToEdit ? 'Temporada actualizada' : 'Temporada creada correctamente');
  };

  const TemporadaCard = ({ temporada }) => {
    const fechaInicio = parseLocalDate(temporada.fecha_inicio) || new Date();
    const fechaFin = parseLocalDate(temporada.fecha_fin) || new Date();
    const rangoFechas = `${fechaInicio.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })} · ${fechaFin.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}`;

    return (
      <Card
        className={`h-full border border-border shadow-sm transition-all ${
          temporada.estado === 'cerrada' ? 'opacity-60' : 'hover:shadow-md'
        }`}
      >
        <CardContent className="p-5 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Calendar className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-lg font-semibold text-foreground">
                  {temporada.nombre}
                </CardTitle>
                <p className="text-sm text-muted-foreground">{rangoFechas}</p>
              </div>
            </div>
            <Badge
              variant={
                temporada.estado === 'abierta' ? 'success'
                : temporada.estado === 'planeada' ? 'secondary'
                : 'outline'
              }
            >
              {ESTADO_LABELS[temporada.estado] || temporada.estado}
            </Badge>
          </div>

          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
            <Badge variant="outline" className="border-dashed text-xs">
              Duración: {Math.max(1, Math.ceil((fechaFin - fechaInicio) / (1000 * 60 * 60 * 24)))} días
            </Badge>
            {temporada.deporte_id && (
              <Badge variant="outline" className="border-dashed text-xs capitalize">
                {deporteLabel(temporada.deporte_id)}
              </Badge>
            )}
            {temporada.descripcion && (
              <Badge variant="outline" className="border-none bg-muted/50 text-xs">
                {temporada.descripcion}
              </Badge>
            )}
          </div>

          <div className="flex gap-2 pt-2 border-t border-dashed border-border">
            <Button
              size="sm"
              className="flex-1"
              onClick={() => navigate(`/admin/inscripciones?temporada=${temporada.id}`)}
            >
              <Users className="h-4 w-4 mr-1.5" />
              Inscritos
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="flex-1"
              disabled={!puedeEditarTemporada(temporada)}
              onClick={() => handleEditarTemporada(temporada)}
            >
              <Edit2 className="h-4 w-4 mr-1.5" />
              Editar
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-9 w-9 p-0 shrink-0" title="Más acciones">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {temporada.estado !== 'cerrada' && (
                  <DropdownMenuItem
                    onClick={() =>
                      handleCambiarEstado(temporada, temporada.estado === 'abierta' ? 'cerrada' : 'abierta')
                    }
                  >
                    <Power className="h-4 w-4" />
                    {temporada.estado === 'abierta' ? 'Cerrar temporada' : 'Abrir temporada'}
                  </DropdownMenuItem>
                )}
                {temporada.estado !== 'cerrada' && <DropdownMenuSeparator />}
                <DropdownMenuItem
                  disabled={!puedeEditarTemporada(temporada)}
                  onClick={() => handleEliminarTemporada(temporada)}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                  Eliminar temporada
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (!isAdmin) {
    return (
      <FeaturedLayout>
        <div className="flex items-center justify-center h-full">
          <p className="text-muted-foreground">No tienes permisos para acceder a esta página</p>
        </div>
      </FeaturedLayout>
    );
  }

  const deportesOptions = [...new Map(
    temporadas.filter((t) => t.deporte_id).map((t) => [t.deporte_id, deporteLabel(t.deporte_id)])
  ).entries()].map(([id, nombre]) => ({ id, nombre }));

  const temporadasFiltradas = temporadas.filter((t) => {
    const q = search.trim().toLowerCase();
    if (q && !(t.nombre || '').toLowerCase().includes(q)) return false;
    if (estadoFilter !== 'todas' && t.estado !== estadoFilter) return false;
    if (deporteFilter !== 'todos' && t.deporte_id !== deporteFilter) return false;
    return true;
  });

  const hasActiveFilters = estadoFilter !== 'todas' || deporteFilter !== 'todos';

  return (
    <FeaturedLayout
      title="Temporadas"
      subtitle="Gestiona temporadas para clasificar series de entrenamientos"
      refreshAction={loadTemporadas}
      refreshAction={loadTemporadas}
    >
      {/* Toolbar */}
      <div className="mb-6">
        <div className="flex items-center gap-2 flex-nowrap">
          <div className="flex-1 min-w-0 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              type="text"
              placeholder="Buscar temporada..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground hidden sm:block">
              <span className="font-medium text-foreground">{temporadasFiltradas.length}</span> temporadas
            </span>
            <Button
              variant="outline"
              size="default"
              onClick={() => setShowFilters(!showFilters)}
              className="relative h-10 w-10 p-0 sm:h-10 sm:w-auto sm:px-3"
            >
              <Filter className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Filtros</span>
              {showFilters ? <ChevronUp className="h-4 w-4 ml-2 hidden sm:inline" /> : <ChevronDown className="h-4 w-4 ml-2 hidden sm:inline" />}
            </Button>
            <Button onClick={handleNuevaTemporada} className="h-10" aria-label="Nueva temporada" title="Nueva temporada">
              <Plus className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Nueva temporada</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="mb-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-foreground">Estado</Label>
              <Select value={estadoFilter} onValueChange={setEstadoFilter}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas</SelectItem>
                  <SelectItem value="planeada">Planeada</SelectItem>
                  <SelectItem value="abierta">Abierta</SelectItem>
                  <SelectItem value="cerrada">Cerrada</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-foreground">Deporte</Label>
              <Select value={deporteFilter} onValueChange={setDeporteFilter}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  {deportesOptions.map((d) => <SelectItem key={d.id} value={d.id}>{d.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {hasActiveFilters && (
            <div className="flex justify-end">
              <Button variant="outline" onClick={() => { setEstadoFilter('todas'); setDeporteFilter('todos'); }}>
                Limpiar filtros
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Lista de temporadas */}
      {(() => {
        const temporadasVisibles = temporadasFiltradas;
        return loading ? (
          <div className="flex items-center justify-center py-32">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Cargando temporadas...</p>
            </div>
          </div>
        ) : temporadasVisibles.length === 0 ? (
          <div className="flex items-center justify-center py-32">
            <div className="text-center px-6 max-w-md">
              <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-muted">
                <Calendar className="h-10 w-10 text-muted-foreground" />
              </div>
              <h3 className="mb-2 text-xl font-semibold text-foreground">No hay temporadas</h3>
              <p className="mb-6 text-muted-foreground">
                No hay temporadas creadas. Crea tu primera temporada para clasificar tus series de entrenamiento.
              </p>
              <Button onClick={handleNuevaTemporada}>
                <Plus className="h-4 w-4 mr-2" />
                Crear Primera Temporada
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {temporadasVisibles.map((temporada) => (
              <TemporadaCard key={temporada.id} temporada={temporada} />
            ))}
          </div>
        );
      })()}

      {/* Modal de formulario */}
      <FormTemporadaModal
        open={showFormModal}
        onOpenChange={setShowFormModal}
        temporada={temporadaToEdit}
        onSuccess={handleFormSuccess}
      />

      {/* Modal general */}
      <Modal
        isOpen={modal.isOpen}
        onClose={closeModal}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        confirmText={modal.confirmText}
        cancelText={modal.cancelText}
        onConfirm={modal.onConfirm}
        onCancel={modal.onCancel}
        showCancel={modal.showCancel}
      />
    </FeaturedLayout>
  );
};

// Componente de formulario
const FormTemporadaModal = ({ open, onOpenChange, temporada, onSuccess }) => {
  const { org } = useOrganization();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [orgDeportes, setOrgDeportes] = useState([]);
  const [deportesCargados, setDeportesCargados] = useState(false);
  const { label: nombreDeporte } = useDeportes();
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    fecha_inicio: getMexicoDateString(),
    fecha_fin: '',
    deporte_id: '',
    estado: 'planeada',
  });

  useEffect(() => {
    if (!open || !org?.id) return;
    getOrgDeportes(org.id).then(({ data }) => {
      if (data) setOrgDeportes(data);
      setDeportesCargados(true);
    });
  }, [open, org?.id]);

  useEffect(() => {
    if (temporada) {
      setFormData({
        nombre: temporada.nombre || '',
        descripcion: temporada.descripcion || '',
        fecha_inicio: temporada.fecha_inicio || getMexicoDateString(),
        fecha_fin: temporada.fecha_fin || '',
        deporte_id: temporada.deporte_id || '',
        estado: temporada.estado || 'planeada',
      });
    } else {
      setFormData({
        nombre: '',
        descripcion: '',
        fecha_inicio: getMexicoDateString(),
        fecha_fin: '',
        deporte_id: '',
        estado: 'planeada',
      });
    }
    setError(null);
  }, [temporada, open]);

  const guard = useUnsavedGuard({ onClose: () => onOpenChange(false) });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validaciones
    if (!formData.nombre.trim()) {
      setError('El nombre es requerido');
      return;
    }

    if (!formData.fecha_inicio) {
      setError('La fecha de inicio es requerida');
      return;
    }

    if (!formData.fecha_fin) {
      setError('La fecha de fin es requerida');
      return;
    }

    if (formData.fecha_fin <= formData.fecha_inicio) {
      setError('La fecha de fin debe ser posterior a la fecha de inicio');
      return;
    }

    if (!formData.deporte_id) {
      setError('El deporte es requerido');
      return;
    }

    setLoading(true);

    try {
      let result;
      if (temporada) {
        result = await updateTemporada(temporada.id, formData);
      } else {
        result = await createTemporada(formData);
      }

      if (result.error) {
        throw result.error;
      }

      if (onSuccess) {
        onSuccess(result.data);
      }

      guard.resetDirty();
      onOpenChange(false);
    } catch (err) {
      console.error('Error guardando temporada:', err);
      setError(err.message || 'Error al guardar la temporada');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={guard.onOpenChange}>
      <DialogContent {...guard.dirtyProps} className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {temporada ? 'Editar Temporada' : 'Nueva Temporada'}
          </DialogTitle>
          <DialogDescription>
            {temporada ? 'Modifica los datos de la temporada' : 'Crea una nueva temporada para clasificar tus series de entrenamientos'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Nombre */}
          <div className="space-y-2">
            <Label htmlFor="nombre">Nombre *</Label>
            <Input
              id="nombre"
              value={formData.nombre}
              onChange={(e) => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
              placeholder="Ej: Julio - Diciembre 2025"
              required
            />
          </div>

          {/* Descripción */}
          <div className="space-y-2">
            <Label htmlFor="descripcion">Descripción</Label>
            <Textarea
              id="descripcion"
              value={formData.descripcion}
              onChange={(e) => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
              placeholder="Descripción opcional..."
              className="min-h-[80px]"
            />
          </div>

          {/* Fechas */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="fecha_inicio">Fecha Inicio *</Label>
              <Input
                id="fecha_inicio"
                type="date"
                value={formData.fecha_inicio}
                onChange={(e) => setFormData(prev => ({ ...prev, fecha_inicio: e.target.value }))}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="fecha_fin">Fecha Fin *</Label>
              <Input
                id="fecha_fin"
                type="date"
                value={formData.fecha_fin}
                onChange={(e) => setFormData(prev => ({ ...prev, fecha_fin: e.target.value }))}
                required
              />
            </div>
          </div>

          {/* Deporte */}
          <div className="space-y-2">
            <Label htmlFor="deporte_id">Deporte *</Label>
            {temporada?.id ? (
              <DeporteFijo id="deporte_id" nombre={nombreDeporte(formData.deporte_id)} crea="otra temporada" />
            ) : (
              <>
                <Select
                  value={formData.deporte_id}
                  onValueChange={(v) => {
                    guard.markDirty();
                    setFormData(prev => ({ ...prev, deporte_id: v }));
                  }}
                >
                  <SelectTrigger id="deporte_id">
                    <SelectValue placeholder="Selecciona un deporte" />
                  </SelectTrigger>
                  <SelectContent>
                    {opcionesDeporte(orgDeportes, formData.deporte_id, nombreDeporte).map((o) => (
                      <SelectItem key={o.value} value={o.value} disabled={o.deshabilitado}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {deportesCargados && orgDeportes.length === 0 && (
                  <p className="text-xs text-muted-foreground">{SIN_DEPORTES_MSG}</p>
                )}
              </>
            )}
          </div>

          {/* Botones */}
          <div className="flex gap-3 justify-end pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => guard.onOpenChange(false)}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
            >
              {loading ? 'Guardando...' : temporada ? 'Actualizar' : 'Crear Temporada'}
            </Button>
          </div>
        </form>
        {guard.DiscardDialog}
      </DialogContent>
    </Dialog>
  );
};

export default TemporadasManager;
